import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';

function BulkImport({ onClose, onSuccess }) {
  const [step,        setStep]        = useState(1); // 1=upload, 2=preview, 3=done
  const [file,        setFile]        = useState(null);
  const [preview,     setPreview]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [result,      setResult]      = useState(null);
  const [error,       setError]       = useState('');
  const [planInfo,    setPlanInfo]     = useState(null);
  const [dragOver,    setDragOver]    = useState(false);
  const fileRef = useRef();

  // ✅ Load plan info on mount
  useEffect(() => {
    api.get('/items/plan-info')
      .then(r => setPlanInfo(r.data))
      .catch(() => {});
  }, []);

  // ✅ Export CSV
  const exportCSV = () => {
    const token = localStorage.getItem('token');
    window.open(
      `/api/items/export-csv?token=${token}`,
      '_blank'
    );
    // OR use api directly:
    api.get('/items/export-csv', { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a   = document.createElement('a');
        a.href    = url;
        a.download = 'jsmstock_items.csv';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Export failed!'));
  };

  // ✅ Download template
  const downloadTemplate = () => {
    const wb  = XLSX.utils.book_new();
    const ws  = XLSX.utils.aoa_to_sheet([
      ['name', 'name_tamil', 'quantity', 'price', 'mrp', 'low_stock_alert', 'barcode'],
      ['Rice 1kg', 'அரிசி 1kg', '100', '60', '65', '10', ''],
      ['Sugar 1kg', 'சர்க்கரை 1kg', '50', '45', '48', '5', ''],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'jsmstock_template.xlsx');
  };

  // ✅ File size per plan!
  const MAX_SIZE_MB = planInfo?.max_file_size_mb || 10;

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    // ✅ Plan-based file size check
    const sizeMB = selectedFile.size / (1024 * 1024);
    if (sizeMB > MAX_SIZE_MB) {
      setError(
        `File too large! ${planInfo?.plan_name || 'Your plan'} = Max ${MAX_SIZE_MB}MB. ` +
        `Your file: ${sizeMB.toFixed(1)}MB. ` +
        (MAX_SIZE_MB < 25 ? 'Premium plan = 25MB limit!' : '')
      );
      return;
    }

    // File type check
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx','xls','csv'].includes(ext)) {
      setError('Only Excel (.xlsx/.xls) or CSV files allowed!');
      return;
    }

    setFile(selectedFile);
    setError('');
    parseFile(selectedFile);
  };

  const parseFile = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const raw  = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (raw.length === 0) {
          setError('File empty! Data இல்லை!'); return;
        }

        // ✅ Check import limit
        const maxRows = planInfo?.max_import_rows || 1000;
        if (raw.length > maxRows) {
          setError(
            `Too many rows! Max ${maxRows} items import பண்ணலாம். ` +
            `Your file has ${raw.length} rows. ` +
            (planInfo?.plan === 'trial' || planInfo?.plan === 'pro'
              ? 'Upgrade to Premium for 3,000 items!'
              : 'Remove some rows!')
          );
          return;
        }

        const getVal = (row, ...keys) => {
          for (const k of keys) {
            const found = Object.keys(row).find(
              rk => rk.toLowerCase().replace(/\s+/g,'_') ===
                    k.toLowerCase().replace(/\s+/g,'_')
            );
            if (found && row[found] !== '') return row[found];
          }
          return '';
        };

        const parsed = raw.map((row, i) => ({
          row:             i + 2,
          name:            String(getVal(row,'name','item_name','product','stock_item','medicine_name','item','description') || '').trim(),
          name_tamil:      String(getVal(row,'name_tamil','tamil_name','tamil') || '').trim(),
          quantity:        parseFloat(getVal(row,'quantity','qty','stock_qty','closing_balance','current_stock','avl_qty','stock') || 0) || 0,
          price:           parseFloat(getVal(row,'price','rate','sale_price','selling_price','ptr','mrp2') || 0) || 0,
          mrp:             parseFloat(getVal(row,'mrp','maximum_retail_price','retail_price') || 0) || 0,
          low_stock_alert: parseFloat(getVal(row,'low_stock_alert','min_qty','reorder','alert') || 10) || 10,
          barcode:         String(getVal(row,'barcode','ean','upc','code') || '').trim(),
        })).filter(r => r.name);

        if (parsed.length === 0) {
          setError('No valid items found! "name" column required!'); return;
        }

        setPreview(parsed);
        setStep(2);
      } catch (err) {
        setError('File parse failed! Excel/CSV format check பண்ணுங்கள்!');
      }
    };
    reader.readAsBinaryString(f);
  };

  const confirmImport = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post('/items/bulk-import', {
        items: preview.map(r => ({
          name:            r.name,
          name_tamil:      r.name_tamil,
          quantity:        r.quantity,
          price:           r.price,
          mrp:             r.mrp || null,
          low_stock_alert: r.low_stock_alert,
          barcode:         r.barcode || null,
        }))
      });
      setResult(res.data);
      setStep(3);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed!');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setFile(null);
    setPreview([]); setResult(null);
    setError('');
  };

  // ── Plan info bar ────────────────────────────────
  const PlanBar = () => {
    if (!planInfo) return null;
    const pct = planInfo.used_percent || 0;
    const barColor = pct >= 90 ? '#E74C3C' :
                     pct >= 70 ? '#E67E22' : '#27AE60';
    return (
      <div style={{
        background:'#F8F9FA',borderRadius:'10px',
        padding:'12px 16px',marginBottom:'16px',
        border:'1px solid #EEE',
      }}>
        <div style={{display:'flex',justifyContent:'space-between',
          fontSize:'13px',marginBottom:'8px'}}>
          <span style={{fontWeight:'700',color:'#2C3E50'}}>
            {planInfo.plan_name}
          </span>
          <span style={{color:'#666'}}>
            {planInfo.current_count} / {planInfo.max_items} items used
          </span>
        </div>
        {/* Progress bar */}
        <div style={{
          background:'#E0E0E0',borderRadius:'10px',
          height:'8px',overflow:'hidden',
        }}>
          <div style={{
            width:`${Math.min(pct,100)}%`,
            background:barColor,
            height:'100%',borderRadius:'10px',
            transition:'width 0.3s',
          }}/>
        </div>
        <div style={{
          display:'flex',justifyContent:'space-between',
          fontSize:'12px',marginTop:'6px',
        }}>
          <span style={{color:barColor,fontWeight:'600'}}>
            {pct}% used
          </span>
          <span style={{color:'#27AE60',fontWeight:'600'}}>
            ✅ {planInfo.remaining} slots remaining
          </span>
        </div>
        {/* Warning if near limit */}
        {pct >= 90 && (
          <div style={{
            marginTop:'8px',padding:'8px 12px',
            background:'#FFF0F0',borderRadius:'8px',
            fontSize:'12px',color:'#E74C3C',fontWeight:'600',
          }}>
            ⚠️ Almost full! Upgrade to {
              planInfo.plan === 'pro' ? 'Premium (3,000 items)' :
              planInfo.plan === 'trial' ? 'Pro (1,000 items)' :
              'Business (10,000 items)'
            }!
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <h2 style={S.headerTitle}>📥 Bulk Import / Export</h2>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Step 1 — Upload */}
        {step === 1 && (<>

          {/* Export button */}
          <button style={S.exportBtn} onClick={exportCSV}>
            📤 எல்லா Items Export பண்ணுங்கள் (CSV)
          </button>

          <div style={S.orDivider}>அல்லது</div>

          {/* ✅ Plan info bar */}
          <PlanBar />

          {/* How to import */}
          <div style={S.howTo}>
            <h4 style={{margin:'0 0 8px',color:'#2C3E50',fontSize:'13px'}}>
              📋 எப்படி Import பண்றது?
            </h4>
            {[
              'Excel template download பண்ணுங்கள்',
              'உங்கள் items அதில் fill பண்ணுங்கள்',
              'File-ஐ இங்கே upload பண்ணுங்கள்',
              'Preview பார்த்து Import பண்ணுங்கள்!',
            ].map((s,i)=>(
              <p key={i} style={{margin:'4px 0',fontSize:'13px',color:'#555'}}>
                {i+1}. {s}
              </p>
            ))}
          </div>

          {/* ✅ File limits info */}
          <div style={S.limitInfo}>
            <div style={{display:'flex',gap:'16px',flexWrap:'wrap'}}>
              <span>📁 Max file: <b>{planInfo?.max_file_size_mb || 10}MB</b></span>
              <span>📊 Max rows: <b>{planInfo?.max_import_rows || 1000}</b></span>
              <span>📦 Limit: <b>{planInfo?.max_items || 1000} items</b></span>
              <span>✅ Free: <b>{planInfo?.remaining || 0} slots</b></span>
            </div>
          </div>

          {/* Template download */}
          <button style={S.templateBtn} onClick={downloadTemplate}>
            📋 Template Download பண்ணுங்கள் (Excel)
          </button>

          {/* Drop zone */}
          <div
            style={{...S.dropZone, borderColor:dragOver?'#3498DB':'#DDD',
              background:dragOver?'#EBF5FB':'white'}}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{
              e.preventDefault();setDragOver(false);
              handleFile(e.dataTransfer.files[0]);
            }}
            onClick={()=>fileRef.current.click()}>
            <div style={{fontSize:'36px'}}>📤</div>
            <p style={{margin:'8px 0 4px',fontWeight:'600',color:'#2C3E50'}}>
              Excel/CSV file Click பண்ணி Select பண்ணுங்கள்
            </p>
            <p style={{margin:0,fontSize:'12px',color:'#888'}}>
              .xlsx, .xls, .csv files supported
            </p>
            <input ref={fileRef} type="file"
              accept=".xlsx,.xls,.csv"
              style={{display:'none'}}
              onChange={e=>handleFile(e.target.files[0])}/>
          </div>

          {file && (
            <div style={S.fileInfo}>
              📄 {file.name} ({(file.size/1024).toFixed(1)} KB)
            </div>
          )}

          {error && <div style={S.errorBox}>{error}</div>}
        </>)}

        {/* Step 2 — Preview */}
        {step === 2 && (<>
          <PlanBar />
          <div style={S.previewHeader}>
            <div>
              <h3 style={{margin:0,color:'#2C3E50'}}>
                Preview — {preview.length} items
              </h3>
              <p style={{margin:'4px 0 0',fontSize:'13px',color:'#666'}}>
                Import பண்றதற்கு முன் check பண்ணுங்கள்!
              </p>
            </div>
            <button style={S.backBtn} onClick={reset}>← Back</button>
          </div>

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr style={{background:'#2C3E50'}}>
                  {['#','Name','Tamil','Qty','Price','MRP','Alert'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.slice(0,50).map((item,i)=>(
                  <tr key={i} style={{background:i%2===0?'white':'#F8F9FA'}}>
                    <td style={S.td}>{i+1}</td>
                    <td style={{...S.td,fontWeight:'600'}}>{item.name}</td>
                    <td style={S.td}>{item.name_tamil||'—'}</td>
                    <td style={{...S.td,textAlign:'right'}}>{item.quantity}</td>
                    <td style={{...S.td,textAlign:'right'}}>₹{item.price}</td>
                    <td style={{...S.td,textAlign:'right'}}>
                      {item.mrp?`₹${item.mrp}`:'—'}
                    </td>
                    <td style={{...S.td,textAlign:'right'}}>{item.low_stock_alert}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.length > 50 && (
              <p style={{textAlign:'center',padding:'10px',color:'#888',fontSize:'13px'}}>
                ... and {preview.length-50} more items
              </p>
            )}
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            style={{...S.importBtn,opacity:loading?0.6:1}}
            onClick={confirmImport}
            disabled={loading}>
            {loading ? '⏳ Importing...' : `✅ Import ${preview.length} items`}
          </button>
        </>)}

        {/* Step 3 — Done */}
        {step === 3 && result && (<>
          <div style={S.doneBox}>
            <div style={{fontSize:'48px',marginBottom:'12px'}}>🎉</div>
            <h3 style={{color:'#27AE60',margin:'0 0 8px'}}>Import Complete!</h3>
          </div>

          {/* Result stats */}
          <div style={S.resultGrid}>
            {[
              {icon:'✅',label:'Added',    value:result.added,    color:'#27AE60'},
              {icon:'⏭️',label:'Skipped',  value:result.skipped,  color:'#888'},
              {icon:'📦',label:'Total Now',value:result.current_count||'—',color:'#2C3E50'},
              {icon:'🔢',label:'Remaining',value:result.remaining||'—',color:'#3498DB'},
            ].map((r,i)=>(
              <div key={i} style={{
                ...S.resultCard,
                borderTop:`3px solid ${r.color}`,
              }}>
                <div style={{fontSize:'24px'}}>{r.icon}</div>
                <div style={{fontSize:'24px',fontWeight:'900',color:r.color}}>
                  {r.value}
                </div>
                <div style={{fontSize:'12px',color:'#666'}}>{r.label}</div>
              </div>
            ))}
          </div>

          {/* Plan info after import */}
          {result.plan && (
            <div style={{
              background:'#F8F9FA',borderRadius:'10px',
              padding:'12px',marginTop:'12px',
              fontSize:'13px',color:'#555',
              textAlign:'center',
            }}>
              📊 Plan: <b>{result.plan}</b> |
              Limit: <b>{result.plan_limit}</b> |
              Remaining: <b style={{color:'#27AE60'}}>{result.remaining}</b>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div style={{...S.errorBox,marginTop:'12px'}}>
              ⚠️ Some errors:<br/>
              {result.errors.map((e,i)=>(
                <div key={i} style={{fontSize:'12px'}}>{e}</div>
              ))}
            </div>
          )}

          <div style={{display:'flex',gap:'10px',marginTop:'16px'}}>
            <button style={S.backBtn2} onClick={reset}>
              📥 Import More
            </button>
            <button style={S.doneBtn} onClick={onClose}>
              ✅ Done
            </button>
          </div>
        </>)}

      </div>
    </div>
  );
}

const S = {
  overlay:      {position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',
                 zIndex:1000,display:'flex',alignItems:'center',
                 justifyContent:'center',padding:'16px'},
  modal:        {background:'white',borderRadius:'16px',padding:'24px',
                 maxWidth:'640px',width:'100%',maxHeight:'90vh',
                 overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'},
  header:       {display:'flex',justifyContent:'space-between',
                 alignItems:'center',marginBottom:'20px'},
  headerTitle:  {margin:0,color:'#2C3E50',fontSize:'20px'},
  closeBtn:     {background:'none',border:'none',fontSize:'20px',
                 cursor:'pointer',color:'#888',padding:'4px 8px'},
  exportBtn:    {width:'100%',background:'#3498DB',color:'white',
                 border:'none',padding:'14px',borderRadius:'10px',
                 fontSize:'15px',fontWeight:'700',cursor:'pointer',
                 marginBottom:'12px'},
  orDivider:    {textAlign:'center',color:'#888',
                 fontSize:'13px',margin:'8px 0'},
  howTo:        {background:'#F8F9FA',borderRadius:'10px',
                 padding:'14px',marginBottom:'12px'},
  limitInfo:    {background:'#EBF5FB',borderRadius:'8px',
                 padding:'10px 14px',marginBottom:'12px',
                 fontSize:'13px',color:'#2C3E50',
                 border:'1px solid #BDE0F5'},
  templateBtn:  {width:'100%',background:'#27AE60',color:'white',
                 border:'none',padding:'12px',borderRadius:'10px',
                 fontSize:'14px',fontWeight:'700',cursor:'pointer',
                 marginBottom:'12px'},
  dropZone:     {border:'2px dashed #DDD',borderRadius:'12px',
                 padding:'30px',textAlign:'center',cursor:'pointer',
                 transition:'all 0.2s'},
  fileInfo:     {marginTop:'8px',padding:'8px 12px',background:'#F8F9FA',
                 borderRadius:'8px',fontSize:'13px',color:'#2C3E50'},
  errorBox:     {background:'#FFF0F0',border:'1px solid #E74C3C',
                 color:'#E74C3C',padding:'10px 14px',borderRadius:'8px',
                 marginTop:'12px',fontSize:'13px'},
  previewHeader:{display:'flex',justifyContent:'space-between',
                 alignItems:'flex-start',marginBottom:'14px'},
  backBtn:      {background:'#F8F9FA',border:'1px solid #DDD',
                 padding:'8px 16px',borderRadius:'8px',
                 cursor:'pointer',fontSize:'13px',fontWeight:'600'},
  tableWrap:    {border:'1px solid #EEE',borderRadius:'10px',
                 overflow:'auto',maxHeight:'300px',marginBottom:'14px'},
  table:        {width:'100%',borderCollapse:'collapse'},
  th:           {padding:'10px 12px',color:'white',fontSize:'12px',
                 textAlign:'left',fontWeight:'700'},
  td:           {padding:'8px 12px',fontSize:'13px',
                 borderBottom:'1px solid #F0F0F0'},
  importBtn:    {width:'100%',background:'#27AE60',color:'white',
                 border:'none',padding:'14px',borderRadius:'10px',
                 fontSize:'15px',fontWeight:'700',cursor:'pointer'},
  doneBox:      {textAlign:'center',marginBottom:'16px'},
  resultGrid:   {display:'grid',gridTemplateColumns:'repeat(4,1fr)',
                 gap:'10px',marginBottom:'12px'},
  resultCard:   {background:'#F8F9FA',borderRadius:'10px',
                 padding:'14px',textAlign:'center'},
  backBtn2:     {flex:1,padding:'12px',borderRadius:'8px',
                 border:'1px solid #DDD',background:'white',
                 cursor:'pointer',fontWeight:'600',color:'#555'},
  doneBtn:      {flex:2,padding:'12px',borderRadius:'8px',
                 border:'none',background:'#27AE60',color:'white',
                 cursor:'pointer',fontWeight:'700',fontSize:'14px'},
};

export default BulkImport;

